/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import { find } from 'lodash';
import videojs from 'video.js';
import type { Player } from 'video.js';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import userAgent from 'js/constants/userAgent';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import supportsNativeTextTracks from 'pages/open-course/video/util/supportsNativeTextTracks';

import playerConfig from 'bundles/general-video-player/components/constants';
import useEventCallback from 'bundles/general-video-player/utils/useEventCallback';
import { CrossBrowserFullscreenUtils } from 'bundles/video-player';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { getDefaultVideoResolution } from 'bundles/video-player/utils/videoUtils';
import { useVideoTrimmingContext } from 'bundles/video-trim';
// Need to import these libraries to enable cuepoints
import 'bundles/videojs/plugins/cuepoints';
import 'bundles/videojs/plugins/eventEmitter';
import 'bundles/videojs/plugins/heartbeat';
import 'bundles/videojs/plugins/hotkeys';
import 'bundles/videojs/plugins/joinEvents';
import 'bundles/videojs/plugins/resolutions';
import 'bundles/videojs/plugins/waitEvents';
import { handleVideoJsError } from 'bundles/videojs/utils';

import _t from 'i18n!nls/general-video-player';

import 'css!bundles/videojs/css/coursera';
import 'css!video.js/dist/video-js';
import 'css!video.js/dist/video-js.css';

import useFullscreenObserver from '../utils/useFullscreenObserver';
import FixedAspectRatioBox from './FixedAspectRatioBox';
import VideoPlaceholder from './VideoPlaceholder';
import VideoControlsContainer from './control-bar/VideoControlsContainer';

export type Cuepoint = {
  at: number | string; // number or string(percentage)
  to: number | string; // number or string(percentage)
  callback: () => void;

  // whether to show a marker
  // e.g. used by IVQ
  marker?: boolean;
};

export type TextTrack = {
  label: string;
  language: string;
  src: string;
};

export type VideoSource = {
  type: string;
  src: string;
};

export type Props = {
  onBeforePlay?: () => Promise<boolean>;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onSeeking?: () => void;
  onReady?: () => void;
  onError?: () => void;
  onLoadedMetadata?: () => void;
  sentryErrorSource?: string;
  onTimeUpdate?: (time: number) => void;
  onDurationChange?: (duration: number) => void;
  onChangeFullScreen?: (isFullScreen: boolean) => void;
  isAutoPlayEnabled?: boolean;
  onAutoPlayChange?: (isAutoPlayEnabled: boolean) => void;
  isOverlayEnabled?: boolean;

  // time update event interval in milliseconds. used to reduce the number of time update events
  timeUpdateInterval?: number;

  startAt?: number;
  textTracks?: Array<TextTrack>;
  sourcesByResolution: Record<string, Array<VideoSource>>;
  previewImagesByResolution: Record<string, string>;

  // should the video be played instantly without changing the autoplay settings.
  // this is used internally to handle the cases of justEnrolledThroughMutation and isPlayNow
  isInstantPlay?: boolean;
};

export type TimeUpdateEventHandler = (time: number) => void;

export type LoadedMetadataEventHandler = () => void;

export type VideoPlayerRef = {
  play: () => Promise<any> | undefined | void;
  pause: () => void;
  seekTo: (time: number) => void;
  reloadSources: () => void;
  addCuepoints: (cuepoints: Array<Cuepoint>) => void;
  addTimeUpdateEventHandler: (handler: TimeUpdateEventHandler) => void;
  removeTimeUpdateEventHandler: (handler?: TimeUpdateEventHandler) => void;
  duration: (seconds?: number) => undefined | number;
  currentTime: (seconds?: number) => undefined | number;
  trigger: (event: string | Event | any, hash?: any) => void;
};

const VideoPlayer = React.forwardRef<VideoPlayerRef, React.PropsWithChildren<Props>>(function VideoPlayer(
  {
    onBeforePlay,
    onPlay,
    onPause,
    onEnded,
    onSeeking,
    onReady,
    onTimeUpdate,
    onDurationChange,
    onChangeFullScreen,
    onError,
    onLoadedMetadata,
    sentryErrorSource,
    isAutoPlayEnabled = false,
    onAutoPlayChange,
    textTracks,
    timeUpdateInterval = 1000,
    sourcesByResolution,
    previewImagesByResolution,
    isOverlayEnabled = false,
    isInstantPlay = false,
    startAt,
    children,
  },
  forwardedRef
) {
  const styles = {
    root: css`
      width: 100%;
      height: 100%;
      position: relative;
      background: radial-gradient(circle, rgb(8 39 78 / 70%) 0%, #08274e 100%);
    `,
    videoWrapper: css`
      width: 100%;
      height: 100%;
      position: relative;

      /* we already used the FixedAspectRatioBox to make sure the video has the correct aspect ratio, so no need to have videojs use the same trick again */
      div[data-vjs-player] {
        height: 100% !important;
        padding-top: 0 !important;
      }
    `,

    videoOverlays: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: calc(100% - 39px);
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      z-index: 10;
    `,
    center: css`
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      height: 100%;
      margin: 0 auto;
    `,
  };

  const { trimmedStartTimeSeconds, trimmedDurationSeconds, trimmedEndTimeSeconds } = useVideoTrimmingContext();

  const rootRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<Player>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const timeRef = React.useRef<number | null>(null);

  // holds time update event handlers
  const timeUpdateHandlersRef = React.useRef<TimeUpdateEventHandler[]>([]);

  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const [shouldShowPreview, setShouldShowPreview] = React.useState(true);

  // hold the video fullscreen state
  const [isVideoFulscreen, setIsVideoFulscreen] = React.useState(false);
  // markers are array of seconds
  const [markers, setMarkers] = React.useState<Array<number>>([]);

  const resolution = userPreferences.get('resolution') || getDefaultVideoResolution();
  const sources = Object.keys(sourcesByResolution).reduce((acc: Array<VideoSource>, resolution: string) => {
    return acc.concat(sourcesByResolution[resolution]);
  }, []);

  const previewImage = previewImagesByResolution[resolution];

  const defaultVjsOptions = {
    preload: 'auto',

    // TODO: changed to 'play' temporarily to make sure the refactored video player works the same as before.
    // in the future this option should made back to `false` and configurable
    autoplay: false,
    controls: false,
    fluid: true,
    html5: {
      nativeTextTracks: supportsNativeTextTracks(),
    },
    sources,
    defaultSubtitleLanguage: userPreferences.get('subtitleLanguage'),
    plugins: {
      hotkeys: {},
      resolutions: {
        sourcesByResolution,
        resolution,
      },
      joinEvents: {},
      waitEvents: userAgent.browser.name !== 'IE' ? {} : undefined,
    },
  };

  // NOTE: All event callbacks that bound to `video.js` must be wrapped with `useEventCallback`.
  // The reason is that `video.js` events are bound with `videojs.on()` method,
  // so the callbacks are "cached" and will not be able to access latest states,
  // similar to a `useCallback` hook.
  //
  // `useEventCallback()` hook uses a ref to make sure the actual callback is updated on every render.
  // See `useEventCallback` implementation for more details.
  const handleEnded = useEventCallback(() => {
    onEnded?.();
  });

  const handleError = useEventCallback(() => {
    if (sentryErrorSource) {
      handleVideoJsError(playerRef.current, sentryErrorSource);
    }

    onError?.();
  });

  const handlePlay = useEventCallback(() => {
    onPlay?.();
  });

  const handleSeeking = useEventCallback(() => {
    onSeeking?.();
  });

  const handlePause = useEventCallback(() => {
    onPause?.();
  });

  const handleTimeUpdate = useEventCallback(() => {
    if (playerRef.current) {
      const t = playerRef.current.currentTime() || 0;
      if (timeRef.current === null || Math.abs(t - timeRef.current) * 1000 > timeUpdateInterval) {
        onTimeUpdate?.(t);

        for (const handler of timeUpdateHandlersRef.current) {
          handler(t);
        }

        timeRef.current = t;
      }
    }
  });

  const handleDurationChange = useEventCallback(() => {
    setIsVideoLoaded(true);
    if (playerRef.current) {
      onDurationChange?.(playerRef.current.duration() || 0);
    }
  });

  const setUserPreferences = () => {
    const playbackRate = userPreferences.get('playbackRate');
    const volume = userPreferences.get('volume');

    // @ts-expect-error TSMIGRATION
    playerRef.current.playbackRate(playbackRate);
    // @ts-expect-error TSMIGRATION
    playerRef.current.volume(volume);
  };

  const initializeSubtitleTracks = () => {
    // @ts-expect-error TSMIGRATION
    const tracks = playerRef.current.textTracks();
    const track = find(tracks, (t) => t.language === userPreferences.get('subtitleLanguage'));
    if (track) {
      track.mode = 'showing';
    }
    // @ts-expect-error TSMIGRATION
    playerRef.current.trigger('trackloaded');
  };

  const handleLoadedMetadata = () => {
    setUserPreferences();
    initializeSubtitleTracks();
    onLoadedMetadata?.();
  };

  const handlePlayButtonClicked = () => {
    if (onBeforePlay) {
      onBeforePlay().then((shouldPlay: boolean) => {
        const currentTime = playerRef.current?.currentTime() ?? 0;
        const trimmedEndTime = trimmedDurationSeconds ? trimmedDurationSeconds + trimmedStartTimeSeconds : undefined;
        const outsideTrimRange = trimmedEndTime && currentTime && currentTime >= trimmedEndTime;
        if (shouldPlay && !outsideTrimRange) {
          setShouldShowPreview(false);
          // @ts-expect-error TSMIGRATION
          playerRef.current.play();

          if (startAt) {
            // @ts-expect-error TSMIGRATION
            playerRef.current.currentTime(startAt);
          }
        }
      });
    } else {
      setShouldShowPreview(false);
      // @ts-expect-error TSMIGRATION
      playerRef.current.play();

      if (startAt) {
        // @ts-expect-error TSMIGRATION
        playerRef.current.currentTime(startAt);
      }
    }
  };

  const addCuepoints = (cuepoints: Array<Cuepoint>) => {
    // add cuepoints
    // @ts-expect-error TSMIGRATION
    const duration = trimmedEndTimeSeconds || playerRef.current.duration();
    const toAbsoluteTime = (t: number | string) => {
      if (typeof t === 'string') {
        if (t.endsWith('%') && typeof duration === 'number') {
          return (duration * parseFloat(t.slice(0, -1))) / 100.0;
        } else {
          return parseFloat(t);
        }
      } else {
        return t;
      }
    };

    cuepoints.forEach((cuepoint) => {
      // @ts-expect-error TSMIGRATION
      playerRef.current.addCuepoint(
        {
          at: toAbsoluteTime(cuepoint.at),
          to: toAbsoluteTime(cuepoint.to),
        },
        cuepoint.callback
      );
    });

    const newMarkers = cuepoints
      .map((cuepoint) =>
        cuepoint.marker ? toAbsoluteTime(toAbsoluteTime(cuepoint.at) - trimmedStartTimeSeconds) : null
      )
      .filter((marker): marker is number => typeof marker === 'number' && marker > 0);

    setMarkers((prevState) => [...prevState, ...newMarkers]);
  };

  const mountVideoPlayer = () => {
    // @ts-expect-error TSMIGRATION
    playerRef.current = videojs(videoRef.current, defaultVjsOptions);

    playerRef.current.ready(() => {
      // setup UI
      // @ts-expect-error TSMIGRATION
      playerRef.current.bigPlayButton.hide();
      // @ts-expect-error TSMIGRATION
      playerRef.current.controlBar.show();
      // @ts-expect-error TSMIGRATION
      playerRef.current.userActive(true);

      // setup events
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('play', handlePlay);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('ended', handleEnded);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('seeking', handleSeeking);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('pause', handlePause);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('timeupdate', handleTimeUpdate);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('durationchange', handleDurationChange);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('loadedmetadata', handleLoadedMetadata);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('error', handleError);
      // @ts-expect-error TSMIGRATION
      playerRef.current.on('keydown', handlePlayerKeyDown);

      onReady?.();

      if (isAutoPlayEnabled || isInstantPlay) {
        // @ts-expect-error TSMIGRATION
        playerRef.current
          .play()
          .then(() => {
            // only remove the preview when play successfully
            // Chrome will not play the video if the user has not interacted with the page: https://goo.gl/xX8pDD
            setShouldShowPreview(false);

            // if startAt is set, seek to the startAt time
            if (startAt) {
              // @ts-expect-error TSMIGRATION
              playerRef.current.currentTime(startAt);
            }
          })
          .catch(() => {
            // do nothing
          });
      }
    });
    document.addEventListener('keydown', handleDocumentKeyDown);

    // Set focus to video player on initial load, to enable keyboard shortcuts for play/pause/seek
    (playerRef.current.el() as HTMLElement).focus();
  };

  const unmountVideoPlayer = () => {
    if (playerRef.current) {
      playerRef.current.dispose();
    }
  };

  /**
   * Change fullscreen state.
   * @param isFullscreen the fullscreen state BEFORE the change
   */
  const handleFullScreenChange = (isFullscreen: boolean) => {
    if (rootRef.current) {
      if (isFullscreen) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          // @ts-expect-error: Safari
        } else if (document.webkitExitFullscreen) {
          // @ts-expect-error: Safari
          document.webkitExitFullscreen();
        }
      } else if (rootRef.current.requestFullscreen) {
        rootRef.current.requestFullscreen();
        // @ts-expect-error: Safari
      } else if (rootRef.current.webkitRequestFullscreen) {
        // @ts-expect-error: Safari
        rootRef.current.webkitRequestFullscreen();
      }
    }

    onChangeFullScreen?.(!isFullscreen);
  };

  React.useEffect(
    () => {
      mountVideoPlayer();
      return () => {
        unmountVideoPlayer();
      };
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // store the fullscreen state
  // Note: Must use useCallback to prevent the event handler inside useFullscreenObserver being bound and unbound on every render
  const handleFullscreenChange = React.useCallback<() => $TSFixMe>(() => {
    const isFullscreen = CrossBrowserFullscreenUtils.isFullScreen();
    setIsVideoFulscreen(isFullscreen);
  }, []);

  useFullscreenObserver(handleFullscreenChange);

  React.useImperativeHandle(forwardedRef, () => ({
    play: () => {
      playerRef.current?.play();
    },
    pause: () => {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    },
    seekTo: (time: number) => {
      if (playerRef.current) {
        playerRef.current.currentTime(time);
      }
    },
    reloadSources: () => {
      if (playerRef.current) {
        playerRef.current.src(sources);
      }
    },
    addCuepoints: (cuepoints: Array<Cuepoint>) => {
      if (playerRef.current) {
        if (isVideoLoaded) {
          addCuepoints(cuepoints);
        } else {
          const setupCuepoints = () => {
            // @ts-expect-error TSMIGRATION
            playerRef.current.off('durationchange', setupCuepoints);
            // add cuepoints
            addCuepoints(cuepoints);
          };

          playerRef.current.on('durationchange', setupCuepoints);
        }
      }
    },

    addTimeUpdateEventHandler: (handler: TimeUpdateEventHandler) => {
      timeUpdateHandlersRef.current.push(handler);
    },

    removeTimeUpdateEventHandler: (handler?: TimeUpdateEventHandler) => {
      if (handler) {
        timeUpdateHandlersRef.current = timeUpdateHandlersRef.current.filter((h) => h !== handler);
      } else {
        timeUpdateHandlersRef.current = [];
      }
    },
    duration: (seconds?: number) => {
      return playerRef.current?.duration(seconds);
    },
    currentTime: (seconds?: number) => {
      return playerRef.current?.currentTime(seconds);
    },
    trigger: (event: string | Event | unknown, hash?: unknown) => {
      playerRef.current?.trigger(event, hash);
    },
  }));

  const togglePlayPause = (): void => {
    const videoPlayer = playerRef.current;
    videoPlayer?.paused() ? handlePlayButtonClicked() : videoPlayer?.pause();
  };

  const handlePlayerKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    const { SEEK_STEP, VOLUME_STEP, MIN_VIDEO_TIME, DEFAULT_VOLUME, START_TIME, MAX_VOLUME } = playerConfig;
    const videoPlayer = playerRef.current;

    const adjustVolume = (increase: boolean) => {
      event.preventDefault();
      const currentVolume = videoPlayer?.volume() ?? DEFAULT_VOLUME;
      const newVolume = Math.max(
        DEFAULT_VOLUME,
        Math.min(MAX_VOLUME, currentVolume + (increase ? VOLUME_STEP : -VOLUME_STEP))
      );

      videoPlayer?.volume(newVolume);
    };

    const seekBackward = (): void => {
      event.preventDefault();
      const videoPositionCurrentTime = videoPlayer?.currentTime() ?? START_TIME;
      const TRIMMED_MIN_VIDEO_TIME = Math.max(MIN_VIDEO_TIME, trimmedStartTimeSeconds);
      const newTime = Math.max(TRIMMED_MIN_VIDEO_TIME, videoPositionCurrentTime - SEEK_STEP);
      videoPlayer?.currentTime(newTime);
    };

    const seekForward = (): void => {
      event.preventDefault();
      const videoPositionCurrentTime = videoPlayer?.currentTime() ?? START_TIME;
      const newTimeWithSeekStep = videoPositionCurrentTime + SEEK_STEP;
      const newTime = newTimeWithSeekStep;
      videoPlayer?.currentTime(newTime);
    };

    switch (key) {
      // Toggle play/pause with the space key
      case ' ':
        event.preventDefault();
        togglePlayPause();
        break;
      // Toggle play/pause with the Enter key
      case 'Enter':
        event.preventDefault();
        togglePlayPause();
        break;
      // Volume up with up arrow
      case 'ArrowUp':
        adjustVolume(true);
        break;
      // Volume down with down arrow
      case 'ArrowDown':
        adjustVolume(false);
        break;
      // Seek with the left arrow key
      case 'ArrowLeft':
        seekBackward();
        break;
      // Seek with the right arrow key
      case 'ArrowRight':
        seekForward();
        break;
      default:
        break;
    }
  };

  const handleDocumentKeyDown = (event: KeyboardEvent) => {
    const { key, shiftKey } = event;
    const { PLAYBACK_LOWER_LIMIT, PLAYBACK_STEP, PLAYBACK_UPPER_LIMIT, DEFAULT_PLAYBACK_RATE } = playerConfig;
    const videoPlayer = playerRef.current;

    const increasePlaybackRate = (): void => {
      event.preventDefault();
      const currentPlaybackRate = videoPlayer?.playbackRate() ?? DEFAULT_PLAYBACK_RATE;
      if (shiftKey && currentPlaybackRate < PLAYBACK_UPPER_LIMIT) {
        const newPlaybackRate = Math.min(PLAYBACK_UPPER_LIMIT, currentPlaybackRate + PLAYBACK_STEP);

        videoPlayer?.playbackRate(newPlaybackRate);
      }
    };

    const decreasePlaybackRate = (): void => {
      event.preventDefault();
      const currentPlaybackRate = videoPlayer?.playbackRate() ?? DEFAULT_PLAYBACK_RATE;
      if (shiftKey && currentPlaybackRate > PLAYBACK_LOWER_LIMIT) {
        const newPlaybackRate = Math.max(PLAYBACK_LOWER_LIMIT, currentPlaybackRate - PLAYBACK_STEP);
        videoPlayer?.playbackRate(newPlaybackRate);
      }
    };

    const toggleFullScreen = (): void => {
      event.preventDefault();
      if (CrossBrowserFullscreenUtils.isFullScreen()) {
        document.exitFullscreen();
      } else {
        handleFullScreenChange(isVideoFulscreen);
      }
    };

    const toggleMute = (): void => {
      event.preventDefault();
      videoPlayer?.muted(!videoPlayer?.muted());
    };

    switch (key) {
      // Toggle play/pause with the k key
      case 'k':
        event.preventDefault();
        togglePlayPause();
        break;
      // Toggle mute with the m key
      case 'm':
        toggleMute();
        break;
      // Toggle fullscreen with the f key
      case 'f':
        toggleFullScreen();
        break;
      // Increase playback rate with Shift + >
      case '>':
        increasePlaybackRate();
        break;
      // Decrease playback rate with Shift + <
      case '<':
        decreasePlaybackRate();
        break;
      default:
        break;
    }
  };

  return (
    // wrap the player in a div with a `data-vjs-player` attribute
    // so videojs won't create additional wrapper in the DOM
    // see https://github.com/videojs/video.js/pull/3856
    <div css={styles.root} ref={rootRef}>
      <FixedAspectRatioBox aspectRatio={16 / 9} isFullscreen={isVideoFulscreen}>
        <div css={styles.center}>
          <div css={styles.videoWrapper}>
            {shouldShowPreview && <VideoPlaceholder src={previewImage} onPlayButtonClicked={handlePlayButtonClicked} />}
            <div data-vjs-player>
              <video
                className={classNames('video-js', 'vjs-big-play-centered', 'vjs-default-skin')}
                ref={videoRef}
                data-e2e="vlp-video-player"
                tabIndex={0}
              >
                {textTracks?.map((track) => (
                  <track key={track.src} kind="captions" label={track.label} srcLang={track.language} src={track.src} />
                ))}

                <p className="vjs-no-js">
                  <FormattedHTMLMessage
                    message={_t(
                      `
                  To view this video please enable JavaScript, and consider upgrading to a web browser that
                  <a href="http://videojs.com/html5-video-support/" target="_blank" rel="noopener noreferrer">
                    supports HTML5 video
                  </a>
                `
                    )}
                  />
                </p>
              </video>
              {playerRef.current && !shouldShowPreview && (
                <VideoControlsContainer
                  player={playerRef.current}
                  markers={markers}
                  isFullscreen={false}
                  showToolbarAutoplayToggle
                  onChangeFullScreen={handleFullScreenChange}
                  defaultSubtitleLanguage={userPreferences.get('subtitleLanguage')}
                  isAutoPlayEnabled={isAutoPlayEnabled}
                  onAutoPlayChange={onAutoPlayChange}
                  trimmedStartTimeSeconds={trimmedStartTimeSeconds}
                  trimmedDurationSeconds={trimmedDurationSeconds}
                />
              )}
            </div>
            {isOverlayEnabled && <div css={styles.videoOverlays}>{children}</div>}
          </div>
        </div>
      </FixedAspectRatioBox>
    </div>
  );
});

export default VideoPlayer;
